import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { motion } from "motion/react";
import { toast } from "sonner";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import {
  BarChart3,
  Home,
  Package,
  PenSquare,
  Receipt,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Lock,
  RefreshCw,
  Search,
  ShieldAlert,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import { adminOverview, adminReprocessCallback, adminSavePackage, adminSetStatus } from "@/lib/admin.functions";
import { formatIDR } from "@/lib/board-constants";
import { SiteFooter, SiteHeader } from "@/components/site-chrome";
import { CountUp, Reveal, ScrollProgress } from "@/components/motion-kit";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { useSession } from "@/lib/auth";
import { RedaksiEditor } from "@/components/redaksi-editor";

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const Route = createFileRoute("/admin")({
  head: () => ({
    meta: [
      { title: "Panel Admin — jasasebar.com" },
      { name: "description", content: "Panel internal untuk moderasi listing, paket promosi, dan monitor pembayaran." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminPage,
});

const PAGE_SIZE = 10;

function AdminPage() {
  const overviewFn = useServerFn(adminOverview);
  const statusFn = useServerFn(adminSetStatus);
  const packageFn = useServerFn(adminSavePackage);
  const reprocessFn = useServerFn(adminReprocessCallback);
  const qc = useQueryClient();

  const session = useSession();
  const [idToken, setIdToken] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [pending, setPending] = useState(false);
  const [listingPage, setListingPage] = useState(1);
  const [ordersPage, setOrdersPage] = useState(1);
  const [eventsPage, setEventsPage] = useState(1);
  const [status, setStatusFilter] = useState<"all" | "approved" | "pending" | "rejected">("all");
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("listings");


  // Admin via Google: ambil ID token lalu masuk otomatis.
  useEffect(() => {
    if (!session.isAdmin) return;
    let alive = true;
    void session.getIdToken().then((tok) => {
      if (!alive || !tok) return;
      setIdToken(tok);
      setPending(true);
      overviewFn({ data: { password: "", idToken: tok, listingPage: 1, ordersPage: 1, eventsPage: 1, pageSize: PAGE_SIZE, status: "all", q: "" } })
        .then(() => setAuthed(true))
        .catch((e: Error) => toast.error(e.message))
        .finally(() => setPending(false));
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.isAdmin]);

  useEffect(() => {
    if (session.isAdmin) return;
    const saved = sessionStorage.getItem("jasasebar:admin");
    if (!saved) return;
    setPassword(saved);
    setPending(true);
    overviewFn({ data: { password: saved, listingPage: 1, ordersPage: 1, eventsPage: 1, pageSize: PAGE_SIZE, status: "all", q: "" } })
      .then(() => setAuthed(true))
      .catch((e: Error) => toast.error(e.message))
      .finally(() => setPending(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cred = idToken ? { password: "", idToken } : { password };

  const key = ["admin", listingPage, ordersPage, eventsPage, status, search];
  const overview = useQuery({
    queryKey: key,
    queryFn: () =>
      overviewFn({
        data: { ...cred, listingPage, ordersPage, eventsPage, pageSize: PAGE_SIZE, status, q: search },
      }),
    enabled: authed,
    staleTime: 20_000,
    placeholderData: (prev) => prev,
  });

  const reload = () => qc.invalidateQueries({ queryKey: ["admin"] });

  const login = useMutation({
    mutationFn: (pw: string) =>
      overviewFn({
        data: { password: pw, listingPage: 1, ordersPage: 1, eventsPage: 1, pageSize: PAGE_SIZE, status: "all", q: "" },
      }),
    onSuccess: () => {
      sessionStorage.setItem("jasasebar:admin", password);
      setAuthed(true);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const setStatusMutation = useMutation({
    mutationFn: (v: { slug: string; status: "approved" | "pending" | "rejected" }) =>
      statusFn({ data: { ...cred, slug: v.slug, status: v.status, note: null } }),
    onSuccess: () => {
      toast.success("Status listing diperbarui.");
      reload();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const savePackage = useMutation({
    mutationFn: (pkg: { id: string; name: string; price: number; days: number; descr: string; active: boolean }) =>
      packageFn({ data: { ...cred, ...pkg } }),
    onSuccess: () => {
      toast.success("Paket promosi tersimpan.");
      reload();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const reprocess = useMutation({
    mutationFn: (key: string) => reprocessFn({ data: { ...cred, key } }),
    onSuccess: ({ result }) => {
      toast.success(result.note);
      reload();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!authed && (session.isLoading || session.isAdmin)) {
    return (
      <div className="min-h-screen">
        <SiteHeader />
        <main className="mx-auto flex max-w-sm flex-col items-center gap-3 px-4 py-24 text-sm text-muted-foreground">
          <Loader2 className="size-5 animate-spin" />
          Menyiapkan panel admin…
        </main>
      </div>
    );
  }

  if (!authed) {

    return (
      <div className="min-h-screen">
        <SiteHeader />
        <main className="mx-auto flex max-w-sm flex-col px-4 py-20">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
            <Card className="p-5">
              <span className="grid size-10 place-items-center rounded-lg bg-secondary">
                <Lock className="size-4 text-primary" />
              </span>
              <h1 className="mt-3 font-display text-xl font-semibold">Panel Admin</h1>
              <p className="mt-1 text-xs text-muted-foreground">
                Akses dibatasi rate limit dan setiap percobaan tercatat di audit log.
              </p>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && login.mutate(password)}
                placeholder="Kata sandi admin"
                className="mt-4 h-9 text-sm"
              />
              <Button
                className="mt-2 h-9 w-full gap-1.5 text-sm"
                disabled={login.isPending || pending}
                onClick={() => login.mutate(password)}
              >
                {(login.isPending || pending) && <Loader2 className="size-4 animate-spin" />} Masuk
              </Button>
            </Card>
          </motion.div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const data = overview.data;

  return (
    <SidebarProvider>
      <ScrollProgress />
      <div className="flex min-h-screen w-full">
        <AdminSidebar tab={tab} onTab={setTab} />
        <SidebarInset className="min-w-0 bg-transparent">
          <header className="sticky top-0 z-30 flex h-14 items-center gap-2 border-b border-border/70 bg-background/70 px-4 backdrop-blur-xl">
            <SidebarTrigger />
            <Separator orientation="vertical" className="mr-1 !h-5" />
            <span className="font-display text-sm font-semibold">Panel Admin</span>
            <Button asChild size="sm" variant="ghost" className="ml-auto h-8 gap-1.5 text-xs">
              <Link to="/">Lihat situs</Link>
            </Button>
          </header>
          <main className="px-4 py-6">

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              <ShieldCheck className="size-3.5" /> Panel admin
            </p>
            <h1 className="font-display text-2xl font-semibold tracking-tight">Kontrol papan</h1>
          </div>
          <div className="flex items-center gap-2">
          <Button size="sm" className="h-8 gap-1.5 text-xs" asChild>
            <Link to="/redaksi">
              <PenSquare className="size-3.5" /> Tulis artikel blog
            </Link>
          </Button>
          <Button size="sm" variant="outline" className="h-8 gap-1.5 text-xs" onClick={reload}>
            <RefreshCw className={`size-3.5 ${overview.isFetching ? "animate-spin" : ""}`} /> Muat ulang
          </Button>
          </div>
        </div>

        {!data ? (
          <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Memuat data…
          </div>
        ) : (
          <>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <Metric label="Total listing" value={data.totals.listings} />
              <Metric label="Menunggu moderasi" value={data.totals.pending} />
              <Metric label="Pendapatan terbayar" value={data.totals.revenue} money />
              <Metric label="Order belum lunas" value={data.totals.unpaid} />
            </div>

            <Tabs value={tab} onValueChange={setTab} className="mt-4">


              <TabsContent value="listings" className="mt-3">
                <Card>
                  <CardHeader className="flex-row flex-wrap items-center justify-between gap-2 space-y-0 p-3">
                    <CardTitle className="text-sm">Moderasi listing</CardTitle>
                    <div className="flex items-center gap-2">
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          value={q}
                          onChange={(e) => setQ(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              setSearch(q);
                              setListingPage(1);
                            }
                          }}
                          placeholder="Cari nama/slug"
                          className="h-8 w-44 pl-7 text-xs"
                        />
                      </div>
                      <Select
                        value={status}
                        onValueChange={(v) => {
                          setStatusFilter(v as typeof status);
                          setListingPage(1);
                        }}
                      >
                        <SelectTrigger className="h-8 w-32 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Semua</SelectItem>
                          <SelectItem value="approved">Approved</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="rejected">Rejected</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y divide-border">
                      {data.listings.map((l) => (
                        <div key={l.slug} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium">{l.name}</p>
                            <p className="truncate text-[11px] text-muted-foreground">
                              {l.slug} · {l.category} · {formatIDR(l.total_amount)} · {l.clicks} klik
                            </p>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <Badge variant="secondary" className="px-1.5 py-0 text-[10px]">
                              {l.status}
                            </Badge>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 gap-1 px-2 text-[11px]"
                              onClick={() => setStatusMutation.mutate({ slug: l.slug, status: "approved" })}
                            >
                              <CheckCircle2 className="size-3 text-primary" /> Setujui
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 gap-1 px-2 text-[11px]"
                              onClick={() => setStatusMutation.mutate({ slug: l.slug, status: "rejected" })}
                            >
                              <XCircle className="size-3 text-destructive" /> Tolak
                            </Button>
                          </div>
                        </div>
                      ))}
                      {data.listings.length === 0 && (
                        <p className="p-4 text-xs text-muted-foreground">Tidak ada listing pada filter ini.</p>
                      )}
                    </div>
                    <Pager
                      page={listingPage}
                      total={data.counts.listings}
                      size={data.counts.pageSize}
                      onChange={setListingPage}
                    />
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="charts" className="mt-3">
                <div className="grid gap-3 lg:grid-cols-2">
                  <Reveal>
                    <Card>
                      <CardHeader className="p-3 pb-0">
                        <CardTitle className="text-sm">Pendapatan 30 hari</CardTitle>
                      </CardHeader>
                      <CardContent className="h-56 p-2">
                        {data.revenue.length === 0 ? (
                          <p className="p-3 text-xs text-muted-foreground">Belum ada transaksi lunas.</p>
                        ) : (
                          <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={data.revenue}>
                              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                              <XAxis dataKey="day" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                              <YAxis tick={{ fontSize: 10 }} width={60} tickLine={false} axisLine={false} />
                              <Tooltip
                                contentStyle={{ fontSize: 12, borderRadius: 10 }}
                                formatter={(v: number) => formatIDR(v)}
                              />
                              <Area
                                type="monotone"
                                dataKey="total"
                                stroke="var(--primary)"
                                fill="var(--primary)"
                                fillOpacity={0.16}
                                strokeWidth={2}
                              />
                            </AreaChart>
                          </ResponsiveContainer>
                        )}
                      </CardContent>
                    </Card>
                  </Reveal>
                  <Reveal delay={0.05}>
                    <Card>
                      <CardHeader className="p-3 pb-0">
                        <CardTitle className="text-sm">Klik harian papan</CardTitle>
                      </CardHeader>
                      <CardContent className="h-56 p-2">
                        {data.clicks.length === 0 ? (
                          <p className="p-3 text-xs text-muted-foreground">Belum ada klik tercatat.</p>
                        ) : (
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={data.clicks}>
                              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
                              <XAxis dataKey="day" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
                              <YAxis tick={{ fontSize: 10 }} width={34} tickLine={false} axisLine={false} />
                              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 10 }} />
                              <Bar dataKey="total" fill="var(--primary)" radius={[5, 5, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        )}
                      </CardContent>
                    </Card>
                  </Reveal>
                </div>
              </TabsContent>

              <TabsContent value="orders" className="mt-3">
                <Card>
                  <CardContent className="p-0">
                    <div className="divide-y divide-border">
                      {data.orders.map((o) => (
                        <div
                          key={o.merchant_order_id}
                          className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-xs"
                        >
                          <div className="min-w-0">
                            <p className="truncate font-medium">
                              {o.name} · {formatIDR(o.amount)} ·{" "}
                              {o.kind === "bid" ? "bid" : `paket ${o.package_id ?? "-"}`}
                            </p>
                            <p className="truncate text-muted-foreground">
                              {o.merchant_order_id} · {new Date(o.created_at).toLocaleString("id-ID")}
                            </p>
                          </div>
                          <Badge variant={o.status === "PAID" ? "default" : "secondary"} className="px-1.5 py-0">
                            {o.status}
                          </Badge>
                        </div>
                      ))}
                      {data.orders.length === 0 && <p className="p-4 text-xs text-muted-foreground">Belum ada order.</p>}
                    </div>
                    <Pager
                      page={ordersPage}
                      total={data.counts.orders}
                      size={data.counts.pageSize}
                      onChange={setOrdersPage}
                    />
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="events" className="mt-3">
                <Card>
                  <CardContent className="p-0">
                    <div className="divide-y divide-border">
                      {data.callbacks.map((callback) => (
                        <div
                          key={callback.idempotency_key}
                          className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-xs"
                        >
                          <div className="min-w-0">
                            <p className="truncate font-medium">{callback.merchant_order_id}</p>
                            <p className="break-all font-mono text-[10px] text-muted-foreground">
                              {callback.idempotency_key}
                            </p>
                            <p className="text-muted-foreground">
                              kode {callback.result_code ?? "-"} · {callback.attempts} percobaan ·{" "}
                              {new Date(callback.created_at).toLocaleString("id-ID")}
                            </p>
                            {callback.last_error && <p className="mt-1 text-destructive">{callback.last_error}</p>}
                          </div>
                          <div className="flex items-center gap-1.5">
                            <Badge
                              variant={callback.status === "done" ? "default" : callback.status === "failed" ? "destructive" : "secondary"}
                              className="px-1.5 py-0"
                            >
                              {callback.status === "done" ? "processed" : callback.status}
                            </Badge>
                            {(callback.status === "failed" || callback.status === "pending" || callback.status === "processing") && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 gap-1 px-2 text-[11px]"
                                disabled={reprocess.isPending}
                                onClick={() => reprocess.mutate(callback.idempotency_key)}
                              >
                                <RefreshCw className={`size-3 ${reprocess.isPending ? "animate-spin" : ""}`} /> Reprocess
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                      {data.callbacks.length === 0 && (
                        <p className="p-4 text-xs text-muted-foreground">Belum ada callback masuk.</p>
                      )}
                    </div>
                    <Pager
                      page={eventsPage}
                      total={data.counts.events}
                      size={data.counts.pageSize}
                      onChange={setEventsPage}
                    />
                  </CardContent>
                </Card>
                <p className="mt-2 text-[11px] text-muted-foreground">
                  URL callback pembayaran: <span className="font-mono">/api/public/duitku-callback</span>
                </p>
              </TabsContent>

              <TabsContent value="packages" className="mt-3">
                <div className="grid gap-3 md:grid-cols-3">
                  {data.packages.map((p) => (
                    <PackageEditor
                      key={p.id}
                      pkg={p}
                      onSave={(v) => savePackage.mutate(v)}
                      saving={savePackage.isPending}
                    />
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="audit" className="mt-3">
                <Card>
                  <CardHeader className="p-3 pb-1">
                    <CardTitle className="text-sm">Audit akses admin</CardTitle>
                    <p className="text-[11px] text-muted-foreground">
                      Maks. 5 percobaan gagal per IP dalam 15 menit sebelum akses diblokir sementara.
                    </p>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y divide-border">
                      {data.audit.map((a, i) => (
                        <div key={i} className="flex items-center justify-between gap-2 px-3 py-2 text-xs">
                          <div className="min-w-0">
                            <p className="truncate font-medium">
                              {a.action} · {a.ip}
                            </p>
                            <p className="truncate text-muted-foreground">
                              {a.detail ?? "berhasil"} · {new Date(a.created_at).toLocaleString("id-ID")}
                            </p>
                          </div>
                          <Badge variant={a.ok ? "default" : "destructive"} className="gap-1 px-1.5 py-0">
                            {a.ok ? <ShieldCheck className="size-3" /> : <ShieldAlert className="size-3" />}
                            {a.ok ? "ok" : "gagal"}
                          </Badge>
                        </div>
                      ))}
                      {data.audit.length === 0 && <p className="p-4 text-xs text-muted-foreground">Belum ada log.</p>}
                    </div>
                  </CardContent>
                </Card>
               </TabsContent>

              <TabsContent value="blog" className="mt-3">
                <RedaksiEditor cred={idToken ? { password: "", idToken } : { password }} />
              </TabsContent>
            </Tabs>
          </>
        )}
          </main>
        </SidebarInset>
      </div>
    </SidebarProvider>

  );
}

function Pager({
  page,
  total,
  size,
  onChange,
}: {
  page: number;
  total: number;
  size: number;
  onChange: (p: number) => void;
}) {
  const pages = Math.max(Math.ceil(total / size), 1);
  return (
    <div className="flex items-center justify-between border-t border-border px-3 py-2 text-[11px] text-muted-foreground">
      <span>
        Halaman {page} dari {pages} · {total} data
      </span>
      <div className="flex gap-1">
        <Button
          size="sm"
          variant="ghost"
          className="h-6 px-1.5"
          disabled={page <= 1}
          onClick={() => onChange(page - 1)}
        >
          <ChevronLeft className="size-3.5" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-6 px-1.5"
          disabled={page >= pages}
          onClick={() => onChange(page + 1)}
        >
          <ChevronRight className="size-3.5" />
        </Button>
      </div>
    </div>
  );
}

function Metric({ label, value, money }: { label: string; value: number; money?: boolean }) {
  return (
    <Reveal>
      <Card className="p-3">
        <span className="text-xs text-muted-foreground">{label}</span>
        <div className="mt-1 font-display text-xl font-semibold tabular-nums">
          <CountUp value={value} {...(money ? { format: (v: number) => formatIDR(Math.round(v)) } : {})} />
        </div>
      </Card>
    </Reveal>
  );
}

function PackageEditor({
  pkg,
  onSave,
  saving,
}: {
  pkg: { id: string; name: string; price: number; days: number; descr: string; active: boolean };
  onSave: (v: { id: string; name: string; price: number; days: number; descr: string; active: boolean }) => void;
  saving: boolean;
}) {
  const [name, setName] = useState(pkg.name);
  const [price, setPrice] = useState(pkg.price);
  const [days, setDays] = useState(pkg.days);
  const [descr, setDescr] = useState(pkg.descr);
  const [active, setActive] = useState(pkg.active);

  return (
    <Card className="p-3">
      <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{pkg.id}</p>
      <Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 h-8 text-xs" />
      <div className="mt-1.5 flex gap-1.5">
        <Input
          type="number"
          value={price}
          onChange={(e) => setPrice(Number(e.target.value))}
          className="h-8 text-xs"
        />
        <Input
          type="number"
          value={days}
          onChange={(e) => setDays(Number(e.target.value))}
          className="h-8 w-20 text-xs"
        />
      </div>
      <Textarea
        value={descr}
        onChange={(e) => setDescr(e.target.value)}
        rows={3}
        className="mt-1.5 text-xs"
      />
      <label className="mt-1.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} /> Aktif dijual
      </label>
      <Button
        size="sm"
        className="mt-2 h-8 w-full text-xs"
        disabled={saving}
        onClick={() => onSave({ id: pkg.id, name, price, days, descr, active })}
      >
        Simpan paket
      </Button>
    </Card>
  );
}

const ADMIN_TABS = [
  { value: "listings", label: "Moderasi", icon: ShieldCheck },
  { value: "charts", label: "Grafik", icon: BarChart3 },
  { value: "orders", label: "Order", icon: Receipt },
  { value: "events", label: "Callback", icon: RefreshCw },
  { value: "packages", label: "Paket", icon: Package },
  { value: "blog", label: "Blog", icon: PenSquare },
  { value: "audit", label: "Audit", icon: ShieldAlert },
] as const;

function AdminSidebar({ tab, onTab }: { tab: string; onTab: (v: string) => void }) {
  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <div className="flex items-center gap-2 px-2 py-1.5">
          <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-primary text-primary-foreground">
            <ShieldCheck className="size-4" />
          </span>
          <span className="truncate font-display text-sm font-semibold">jasasebar admin</span>
        </div>
      </SidebarHeader>
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Manajemen</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {ADMIN_TABS.map((item) => (
                <SidebarMenuItem key={item.value}>
                  <SidebarMenuButton
                    isActive={tab === item.value}
                    tooltip={item.label}
                    onClick={() => onTab(item.value)}
                  >
                    <item.icon />
                    <span>{item.label}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild tooltip="Kembali ke situs">
              <Link to="/">
                <Home />
                <span>Kembali ke situs</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
